import JSZip from 'jszip'

export interface FileToZip {
  name: string
  url: string
}

/**
 * Creates a zip file containing the provided files
 *
 * @param files Array of file objects with name and url properties
 * @param folderName Optional name for the main folder in the zip
 * @returns Blob of the zip file content or null if no files provided
 */
export const createZipFromFiles = async (files: FileToZip[], folderName?: string): Promise<Blob | null> => {
  if (files.length === 0) {
    return null
  }

  const zip = new JSZip()
  const folder = folderName ? zip.folder(folderName) : zip

  // Create an array to track success/failure of each file fetch
  const fetchResults: { success: boolean; url: string }[] = []

  // Fetch all files and add them to the zip
  const fetchPromises = files.map(async file => {
    try {
      const response = await fetch(file.url)
      if (!response.ok) throw new Error(`Failed to fetch ${file.url}`)
      const blob = await response.blob()
      folder?.file(file.name, blob)
      fetchResults.push({ success: true, url: file.url })
    } catch (error) {
      console.error(`Error downloading file ${file.url}:`, error)
      fetchResults.push({ success: false, url: file.url })
    }
  })

  await Promise.all(fetchPromises)

  // Generate the zip file content
  const content = await zip.generateAsync({ type: 'blob' })

  return content
}

/**
 * Result object returned by the createZipFromFiles function
 */
export interface ZipResult {
  content: Blob | null
  stats: {
    total: number
    success: number
    failed: number
  }
  failedFiles: string[]
}

/**
 * Creates a zip file with statistics about success/failure
 *
 * @param files Array of file objects with name and url properties
 * @param folderName Optional name for the main folder in the zip
 * @returns Object with zip content and statistics
 */
export const createZipWithStats = async (files: FileToZip[], folderName?: string): Promise<ZipResult> => {
  if (files.length === 0) {
    return {
      content: null,
      stats: { total: 0, success: 0, failed: 0 },
      failedFiles: [],
    }
  }

  const zip = new JSZip()
  const folder = folderName ? zip.folder(folderName) : zip

  // Fetch all files and add them to the zip, tracking success/failure
  const fetchResults = await Promise.all(
    files.map(async file => {
      try {
        const response = await fetch(file.url)
        if (!response.ok) throw new Error(`Failed to fetch ${file.url}`)
        const blob = await response.blob()
        folder?.file(file.name, blob)
        return { success: true, url: file.url }
      } catch (error) {
        console.error(`Error downloading file ${file.url}:`, error)
        return { success: false, url: file.url }
      }
    }),
  )

  // Generate the zip file content
  const content = await zip.generateAsync({ type: 'blob' })

  // Calculate statistics
  const failedFiles = fetchResults.filter(result => !result.success).map(result => result.url)

  return {
    content,
    stats: {
      total: files.length,
      success: files.length - failedFiles.length,
      failed: failedFiles.length,
    },
    failedFiles,
  }
}
